(function (root) {
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const DEFAULT_PLAN_ERROR = 'Choose how often you want it delivered.';

  function visible(element) {
    if (!element || element.hidden || element.closest?.('[hidden]')) return false;
    return typeof element.getClientRects !== 'function' || element.getClientRects().length > 0;
  }

  function focusables(scope) {
    return Array.from(scope?.querySelectorAll(FOCUSABLE) || []).filter(visible);
  }

  // Element to move focus to so Tab and Shift+Tab wrap inside the dialog, or null to let the
  // browser move focus normally. Focus outside the list (the dialog itself) goes to an end.
  function trapTarget(items, active, backwards) {
    if (!items.length) return null;
    const index = items.indexOf(active);
    if (backwards) return index <= 0 ? items[items.length - 1] : null;
    return index === -1 || index === items.length - 1 ? items[0] : null;
  }

  function dialogLabel(scope, fallback) {
    const title = scope?.querySelector('.quickadd__title, .product__title h2, h2');
    const text = String(title?.textContent || '').replace(/\s+/g, ' ').trim();
    return text || fallback || 'Quick add';
  }

  // C-10: the same renewal terms line as the product page, for the plan Subscribe will apply.
  function renewalTerms(label, totalCents, money) {
    return 'Renews ' + (label ? 'every ' + label + ' ' : '') + 'at ' + money(totalCents) + ', plus any shipping and tax, until you cancel. Skip, change or cancel any time.';
  }

  // Interval labels per selling plan id, rendered by alac-plan-interval into the card template.
  function planLabel(scope, plan) {
    if (!plan) return '';
    const node = scope?.querySelector('[data-alac-plan-intervals]');
    if (!node) return '';
    if (!node.alacIntervals) {
      try { node.alacIntervals = JSON.parse(node.textContent || '{}'); } catch (error) { node.alacIntervals = {}; }
    }
    return String(node.alacIntervals[String(plan.id)] || '');
  }

  // Source markup for a card's dialog. It lives in an inert <template> (so its ids are not in the
  // page until the single dialog container is filled); a plain element with the same class works too.
  function templateHtml(card) {
    const source = card?.querySelector?.('.quickadd--container');
    if (!source) return '';
    const html = source.innerHTML;
    return typeof html === 'string' ? html : '';
  }

  // Copied galleries can upgrade while their dialog is still display:none. Re-measure each
  // initialized slider after showing the dialog so Dawn does not retain zero visible slides.
  function measureSliders(scope) {
    Array.from(scope?.querySelectorAll?.('slider-component, slideshow-component') || []).forEach(slider => {
      if (!slider.slider || !slider.nextButton || typeof slider.resetPages !== 'function') return;
      try { slider.resetPages(); } catch (_) { /* an optional gallery must not block quick add */ }
    });
  }

  function formatMoney(data) {
    return cents => new Intl.NumberFormat(data?.locale || 'en-US', { style: 'currency', currency: data?.currency || 'USD' }).format(cents / 100);
  }

  // The collection stepper is outside the product form. Copy typed values as well as button changes
  // to its sole submitted quantity field, and reset that field when a pack tile is selected.
  function syncQuantity(scope) {
    const editor = scope?.querySelector('variant-radios .custom-quantity__selector, .custom-quantity__selector.single-variant');
    const field = editor?.querySelector('[name="quantity"]');
    const submitted = scope?.querySelector('product-form [name="quantity"]');
    const custom = !!scope?.querySelector('.option-container-custom input:checked, .custom-quantity__selector.single-variant');
    const quantity = custom ? Math.max(1, Math.floor(Number(field?.value) || 1)) : 1;
    if (submitted) submitted.value = String(quantity);
    return quantity;
  }

  function updateCustomPrice(scope, result, quantity, money) {
    if (!result?.variant) return;
    const editor = scope?.querySelector('variant-radios .custom-quantity__selector, .custom-quantity__selector.single-variant');
    if (!editor) return;
    const total = editor.querySelector('.variant-price');
    if (total) total.textContent = money(result.activePrice * quantity);
    const each = editor.querySelector('.oc__price-per-bundle');
    if (each) each.textContent = money(result.activePrice) + ' each';
    // A singleton may have no unit measurement even when the first (ten-pack) variant has one.
    // Never reuse the first variant's per-ounce figure for a different selection.
    let variant;
    try {
      variant = JSON.parse(scope.querySelector('variant-radios script[type="application/json"]')?.textContent || '[]')
        .find(item => String(item.id) === String(result.variant.id));
    } catch (_) { /* no trustworthy unit measurement */ }
    editor.querySelectorAll('.sa-unitprice').forEach(node => {
      const measure = variant?.unit_price_measurement;
      const valid = Number(variant?.unit_price) > 0 && Number(variant?.price) > 0 && !!measure?.reference_unit;
      node.hidden = !valid;
      if (valid) {
        const reference = (Number(measure.reference_value) > 1 ? Number(measure.reference_value) : '') + measure.reference_unit;
        node.textContent = money(variant.unit_price * result.activePrice / variant.price) + '/' + reference;
      }
      else node.textContent = '';
    });
  }

  // Shows the terms directly above the add-to-cart button while Subscribe is selected with a plan; hides them otherwise.
  function updateRenewal(scope, result, money) {
    const buttons = scope?.querySelector('product-form .product-form__buttons');
    let node = scope?.querySelector('[data-alac-quickadd-renewal]');
    if (!node && buttons) {
      node = buttons.ownerDocument.createElement('p');
      node.className = 'alac-renewal alac-renewal--quickadd';
      node.setAttribute('data-alac-quickadd-renewal', '');
      node.setAttribute('role', 'status');
      buttons.before(node);
    }
    if (!node) return '';
    const plan = result && result.subscribed ? result.chosen : null;
    const quantity = Math.max(1, Number(scope.querySelector('product-form [name="quantity"]')?.value) || 1);
    const text = plan ? renewalTerms(planLabel(scope, plan), plan.price * quantity, money) : '';
    if (node.textContent !== text) node.textContent = text;
    node.hidden = !text;
    return text;
  }

  function createController(doc) {
    const state = { open: false, trigger: null, unsubscribe: null };
    const modal = () => doc.querySelector('.quickadd__modal-container');
    const overlay = () => doc.querySelector('.quickadd__modal-container-overlay');

    function status(scope) {
      let node = scope.querySelector('[data-quickadd-status]');
      if (!node) {
        node = doc.createElement('p');
        node.className = 'quickadd__status visually-hidden';
        node.setAttribute('role', 'alert');
        node.setAttribute('data-quickadd-status', '');
        scope.prepend(node);
      }
      return node;
    }

    function announce(message) {
      const scope = modal();
      if (!scope || !message) return;
      const node = status(scope);
      node.textContent = '';
      // A fresh text node is announced again even when the message repeats.
      setTimeout(() => { node.textContent = message; }, 50);
    }

    function listenForCart() {
      // pubsub.js and constants.js load after this file; PUB_SUB_EVENTS is a script-level const,
      // so it is read by name rather than from window.
      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : root.PUB_SUB_EVENTS;
      const listen = typeof subscribe === 'function' ? subscribe : root.subscribe;
      if (state.unsubscribe || typeof listen !== 'function' || !events) return;
      state.unsubscribe = listen(events.cartUpdate, event => {
        if (!state.open || event?.source !== 'product-form') return;
        // The cart drawer opens next; it should hand focus back to the card's button, not to
        // the add button inside this dialog, which is removed on close.
        const drawer = doc.querySelector('cart-drawer');
        if (drawer && typeof drawer.setActiveElement === 'function' && state.trigger) drawer.setActiveElement(state.trigger);
        close({ restoreFocus: !drawer });
      });
    }

    function open(trigger, html) {
      const scope = modal();
      if (!scope || !html) return false;
      scope.innerHTML = html;
      scope.setAttribute('role', 'dialog');
      scope.setAttribute('aria-modal', 'true');
      scope.setAttribute('tabindex', '-1');
      scope.setAttribute('aria-label', dialogLabel(scope, scope.dataset?.label));
      status(scope);
      const shade = overlay();
      if (shade) { shade.setAttribute('aria-hidden', 'true'); shade.style.display = 'block'; }
      scope.style.display = 'block';
      measureSliders(scope);
      state.open = true;
      state.trigger = trigger || null;
      if (trigger?.setAttribute) trigger.setAttribute('aria-expanded', 'true');
      doc.body?.classList?.add('overflow-hidden');
      doc.body?.classList?.add('alac-quickadd-open');
      listenForCart();
      scope.querySelector('.option-container__first label')?.click();
      scope.scrollTop = 0;
      scope.focus?.();
      return true;
    }

    function close(options = {}) {
      if (!state.open) return false;
      const scope = modal();
      const shade = overlay();
      if (shade) shade.style.display = 'none';
      if (scope) {
        scope.style.display = 'none';
        // Drop the copied markup so its ids do not duplicate the card templates while closed.
        scope.innerHTML = '';
      }
      state.open = false;
      doc.body?.classList?.remove('overflow-hidden');
      doc.body?.classList?.remove('alac-quickadd-open');
      const trigger = state.trigger;
      state.trigger = null;
      if (trigger?.setAttribute) trigger.setAttribute('aria-expanded', 'false');
      if (options.restoreFocus !== false && trigger?.isConnected !== false) trigger?.focus?.();
      return true;
    }

    function onKeydown(event) {
      if (!state.open) return;
      const scope = modal();
      if (!scope) return;
      const active = doc.activeElement;
      const inside = active === scope || scope.contains(active) || active === doc.body || !active;
      // Another dialog (the cart drawer) owns the keyboard while it is open.
      if (!inside || doc.querySelector('cart-drawer.active')) return;
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const target = trapTarget(focusables(scope), active, event.shiftKey);
      if (target) { event.preventDefault(); target.focus(); }
    }

    return { state, open, close, onKeydown, announce, modal };
  }

  const api = { trapTarget, dialogLabel, focusables, createController, templateHtml, measureSliders, renewalTerms, planLabel, updateRenewal, syncQuantity, updateCustomPrice, DEFAULT_PLAN_ERROR };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document || root.alacQuickAddBound) return;
  root.alacQuickAddBound = true;
  root.AlacQuickAdd = api;

  const doc = root.document;
  const controller = createController(doc);
  const modal = controller.modal;
  function refresh() {
    const scope = modal();
    if (!scope || !root.AlacSubscriptionPricing) return;
    const quantity = syncQuantity(scope);
    const result = root.AlacSubscriptionPricing.sync(scope);
    const money = formatMoney(root.AlacSubscriptionPricing.read?.(scope));
    updateCustomPrice(scope, result, quantity, money);
    updateRenewal(scope, result, money);
    return result;
  }
  function schedule() { setTimeout(refresh, 0); setTimeout(refresh, 150); }

  function startSubscription(scope) {
    const pricing = root.AlacSubscriptionPricing;
    const data = pricing?.read?.(scope);
    if (data) {
      const eligible = variant => variant?.available !== false && variant.allocations?.some(pricing.storefrontPlan);
      if (!eligible(pricing.activeVariant(scope, data))) {
        const choice = data.variants.find(variant => eligible(variant) &&
          Array.from(scope.querySelectorAll('.option-container-size [data-vid]')).some(input => input.dataset.vid === String(variant.id)));
        const input = choice && Array.from(scope.querySelectorAll('.option-container-size [data-vid]')).find(node => node.dataset.vid === String(choice.id));
        input?.click();
      }
    }
    scope.querySelector('[name="tabinner"][value="subscribeandsave"]')?.click();
  }

  doc.addEventListener('keydown', controller.onKeydown);

  doc.addEventListener('click', event => {
    const opener = event.target.closest('.button__container .btn-quickadd-trigger');
    if (opener) {
      if (opener.disabled) return;
      const html = templateHtml(opener.closest('.grid__item'));
      if (controller.open(opener, html)) {
        if (opener.dataset.alacPurchaseMode === 'subscribe') startSubscription(modal());
        schedule();
      }
      else {
        // No dialog on this page (or no template for this card): go to the product page instead of doing nothing.
        const details = opener.closest('.button__container')?.querySelector('a.btn-view')?.href;
        if (details) root.location.assign(details);
      }
      return;
    }
    const scope = modal();
    if (!scope || !controller.state.open) return;
    if (event.target.closest('.quickadd__modal-container-overlay, .quickadd__modal-container .btn-close')) {
      controller.close();
      return;
    }
    if (!scope.contains(event.target)) return;

    const variantChoice = event.target.closest('.option-container-size, .option-container-custom');
    if (variantChoice) {
      const id = variantChoice.querySelector('[data-vid]')?.dataset.vid;
      const input = scope.querySelector('.product-variant-id');
      if (id && input) { input.value = id; input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    const cadence = event.target.closest('.recharge-option label');
    if (cadence) {
      const picker = cadence.closest('.recharge-picker__container');
      picker.querySelectorAll('.recharge-option label').forEach(label => label.classList.toggle('selected', label === cadence));
      const custom = picker.querySelector('.custom-quantity__selector');
      if (cadence.dataset.value === 'custom') {
        custom.style.display = 'block';
        picker.querySelectorAll('.recharge-option').forEach(option => option.style.display = 'none');
      } else custom.style.display = 'none';
    }
    const purchase = event.target.closest('.oc__subscription-tabinner-radio-btn');
    if (purchase) {
      const subscribe = purchase.querySelector('[name="tabinner"]')?.value === 'subscribeandsave';
      const picker = scope.querySelector('.recharge-picker__container');
      if (picker) {
        picker.style.display = subscribe ? 'block' : 'none';
        if (subscribe && !picker.querySelector('.recharge-option label.selected')) picker.querySelector('.recharge-option label')?.classList.add('selected');
      }
    }
    if (event.target.closest('.option-container-custom')) {
      scope.querySelectorAll('variant-radios .custom-quantity__selector').forEach(box => box.style.display = 'block');
      scope.querySelectorAll('variant-radios .option-container').forEach(box => box.style.display = 'none');
    }
    const closeEditor = event.target.closest('.btn-close__selector');
    if (closeEditor) root.AlacSubscriptionPricing.closeEditor(scope, closeEditor);
    if (event.target.closest('variant-radios .quantity__button')) {
      setTimeout(() => {
        const quantity = scope.querySelector('variant-radios .quantity__input')?.value;
        const formQuantity = scope.querySelector('.product-quantity');
        if (quantity && formQuantity) { formQuantity.value = quantity; formQuantity.dispatchEvent(new Event('change', { bubbles: true })); }
      }, 0);
    }
    if (event.target.closest('.option-container, .recharge-option, .quantity__button, .oc__subscription-tabinner-radio-btn, .btn-close__selector')) schedule();
  });

  doc.addEventListener('change', event => {
    if (modal()?.contains(event.target) && (['id', 'weeks', 'quantity', 'tabinner'].includes(event.target.name) || event.target.closest('variant-radios'))) schedule();
  });
  doc.addEventListener('input', event => { if (modal()?.contains(event.target) && ['weeks', 'quantity'].includes(event.target.name)) schedule(); });
  doc.addEventListener('submit', event => {
    const scope = modal();
    if (!scope?.contains(event.target)) return;
    const result = refresh();
    if (scope.querySelector('[name="tabinner"][value="subscribeandsave"]:checked') && !result?.chosen) {
      event.preventDefault(); event.stopImmediatePropagation();
      controller.announce(scope.dataset.planError || DEFAULT_PLAN_ERROR);
    }
  }, true);
})(typeof window !== 'undefined' ? window : globalThis);
